<template>
  <div class="overlay modal-overlay" :class="[type, `position-${position}`, { backdrop }]" :style="overlayAnimation" @mousedown="handleOverlayClick">
    <div
      class="custom-modal"
      :class="[
        {
          blocked: blockQuickClose,
          'blocked-animation': blockedAnimationRunning,
          shadowed: !backdrop,
        },
      ]"
      :style="[modalAnimation, modalStyle]"
      @animationend="blockedAnimationRunning = false"
    >
      <!-- Optional header -->
      <div v-if="slots.header" class="header">
        <div class="title">
          <slot name="header" />
        </div>
        <button v-if="dismissible" class="close" @click="disable()">&times;</button>
      </div>
      <button v-else-if="dismissible" class="close close-right" @click="disable()">&times;</button>

      <!-- Body -->
      <div class="body minimalistic" :style="[bodyPadding, bodyStyle]">
        <slot />
      </div>

      <!-- Optional footer -->
      <div v-if="slots.footer" class="footer">
        <slot name="footer" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useTranslation } from '@/composables/useTranslation'
import { useDefaultStore } from '@/store'
import { StyleValue } from 'vue'
import { computed, onMounted, onUnmounted, ref, useSlots, watch } from 'vue'

// -----------
// -- Setup --
// -----------
const props = withDefaults(
  defineProps<{
    modelValue?: boolean
    activated?: boolean
    type?: 'normal' | 'off-canvas'
    position?: 'middle' | 'left' | 'right'
    animation?: string
    deviatingOpeningAnimation?: string
    animationSpeed?: number
    modalStyle?: string | StyleValue
    navigationConfirmBlocksClose?: boolean
    closeImmediately?: boolean
    bodyStyle?: string | StyleValue
    backdrop?: boolean
    blockQuickClose?: boolean
    /**
     * Determines whether overlay click or esc should close this modal
     */
    inBackground?: boolean
    dismissible?: boolean
    blockClose?: boolean
  }>(),
  {
    activated: true,
    type: 'normal',
    position: 'middle',
    animation: 'auto',
    animationSpeed: 0.2,
    dismissible: true,
    backdrop: true,
  },
)

const emit = defineEmits(['close', 'disable', 'update:modelValue'])

// --------------------------
// -- Composables & Stores --
// --------------------------
const { translate, appTranslate } = useTranslation()
const store = useDefaultStore()
const slots = useSlots()

// ---------------
// -- Reactives --
// ---------------
const modalAnimation = ref('')
const overlayAnimation = ref('')
const blockedAnimationRunning = ref(false)

const mainAnimation = computed(() => (props.animation === 'auto' ? (props.position === 'middle' ? 'slide-top' : `slide-${props.position}`) : props.animation))
const bodyPadding = computed((): StyleValue => ({ paddingTop: slots.header ? '.5em' : '2em', paddingBottom: slots.footer ? '.5em' : '2em' }))

// --------------------------------
// -- Watchers & Lifecycle hooks --
// --------------------------------

watch(
  () => props.activated,
  (activated) => {
    if (activated) {
      enable()
    } else {
      disable()
    }
  },
)
onMounted(() => {
  if (props.activated) {
    enable()
  } else {
    disable()
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', closeExportModalListener)
  if (props.backdrop) {
    store.setModalOpen(false)
  }
})

// -------------
// -- Methods --
// -------------

const closeExportModalListener = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && !props.blockClose) {
    handleQuickClose()
  }
}

const enable = () => {
  if (props.animation) {
    overlayAnimation.value = `animation: fade-in ${props.animationSpeed}s linear;`
    modalAnimation.value = `animation: ${props.deviatingOpeningAnimation || mainAnimation.value}-in ${props.animationSpeed}s ease-out forwards;`
  }

  if (props.backdrop) {
    store.setModalOpen(true)
  }
  document.removeEventListener('keydown', closeExportModalListener)
  document.addEventListener('keydown', closeExportModalListener)
}

const disable = () => {
  if (
    !props.navigationConfirmBlocksClose ||
    !store.confirmNavigationFrom ||
    confirm(appTranslate('Your changes might not have been saved. Are you sure you want to leave the page?'))
  ) {
    document.removeEventListener('keydown', closeExportModalListener)
    store.confirmNavigationFrom = undefined

    if (props.animation) {
      overlayAnimation.value = `animation: fade-out ${props.animationSpeed}s linear forwards;`
      modalAnimation.value = `animation: ${mainAnimation.value}-out ${props.animationSpeed}s ease-out forwards;`
    }

    emit('disable')

    setTimeout(
      () => {
        emit('close')
        emit('update:modelValue', false)
        if (props.backdrop) {
          store.setModalOpen(false)
        }
      },
      props.closeImmediately ? 0 : props.animationSpeed * 1000,
    )
  }
}

const handleOverlayClick = (e: MouseEvent) => {
  const target = e.target as HTMLElement

  if (target.classList.contains('modal-overlay') && props.dismissible) {
    handleQuickClose()
  }
}
// handleClose() {
//   if (
//     !this.inBackground &&
//     (!this.blocked || confirm(translate('Your changes might not have been saved. Are you sure you want to leave the page?',)))
//   ) {
//     this.disable()
//   }
// },
const handleQuickClose = () => {
  if (props.blockQuickClose) {
    modalAnimation.value = ''
    blockedAnimationRunning.value = true
  } else if (!props.inBackground) {
    disable()
  }
}

defineExpose({ disable })
</script>

<style lang="scss" scoped>
.backdrop {
  background-color: var(--dimmed);
}

.overlay {
  z-index: 1040; // bootstrap modal default 4.5

  display: flex;
  align-items: center;

  &.normal {
    .custom-modal {
      $width: 1200px;
      max-width: $width;
      width: $width;
    }
  }

  &.off-canvas .custom-modal {
    min-height: 100%;
    max-width: 400px;
    min-width: 320px;
  }

  &.position-right .custom-modal {
    margin-left: auto;
    margin-right: 0;
  }

  &.position-middle {
    justify-content: center;
    padding: 0;

    @media (min-width: 576px) {
      padding: 1em;
    }

    @media (min-width: 768px) {
      padding: 1em 2em;
    }
  }

  .custom-modal {
    z-index: 1050;
    display: flex;
    flex-flow: column;
    height: fit-content;
    max-height: 100%;
    background-color: white;
    position: relative;

    &::before {
      content: '';
      pointer-events: none;
      position: absolute;
      width: 100%; //calc(100% - 4px);
      height: 100%; //calc(100% - 4px);
      border: 2px solid transparent;
      // transition: border 0.2s;
    }

    &.shadowed {
      box-shadow: 0 0 1.5em #0005;
    }

    .header {
      z-index: 2;
      display: flex;
      flex: 0 1 0;
      user-select: none;
      box-shadow: 0 8px 6px -6px #eee;

      .title {
        margin: 0.5em;
        padding-left: 0.5em;
        flex: 1;
        display: flex;
        align-items: flex-end;
      }
    }

    .close {
      font-size: 2em;
      padding: 1rem 1.5rem;
    }
    .close-right {
      position: absolute;
      right: 8px; // width of scrollbar
      z-index: 10;
    }

    .body {
      flex: 1 1 auto;
      overflow-y: auto;

      padding: 0.5em 1em;

      @media (min-width: 576px) {
        padding: 0.5em 2em; // default padding
      }
    }

    .footer {
      z-index: 2;
      background-color: white;
      flex: 0 1 0;
      padding: 1em;
      box-shadow: 0 -8px 6px -6px #eee;

      max-height: calc(100% - 5em);
    }
  }

  .spacer {
    padding: 1em 0;
  }
}

.close {
  user-select: none;
}

.header {
  background-color: var(--theme-bg);
  color: var(--theme-header-colour);

  .close {
    color: var(--theme-header-colour);
    opacity: 1;
    font-weight: 100;
  }
}

.blocked {
  &::before {
    border: 2px solid orange !important;
  }

  &.blocked-animation {
    animation: shake 0.1s cubic-bezier(0.19, 0.49, 0.38, 0.79) forwards;
    animation-iteration-count: 3;
  }
}

@keyframes shake {
  25% {
    transform: translateX(-10px);
  }

  75% {
    transform: translateX(10px);
  }
  100% {
    transform: translateX(0);
  }
}
</style>
